import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { FilterBar } from './FilterBar';
import GlobalEmptyState from './GlobalEmptyState';
import styles from './KnowledgeTile.module.css';

interface Knowledge {
  id: string;
  knowledgeName: string;
  knowledgeDescription?: string;
}

interface KnowledgeTileProps {
  knowledge: Knowledge;
  onClick?: () => void;
}

export function KnowledgeTile({ knowledge, onClick }: KnowledgeTileProps) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const { t } = useLocale();
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [loadingDocumentCount, setLoadingDocumentCount] = useState<boolean>(true);

  // Fetch document count from knowledge/{id}/documents subcollection
  useEffect(() => {
    const fetchDocumentCount = async () => {
      if (!knowledge.id) {
        setDocumentCount(0);
        setLoadingDocumentCount(false);
        return;
      }

      try {
        setLoadingDocumentCount(true);
        const documentsRef = collection(db, 'knowledge', knowledge.id, 'documents');
        const querySnapshot = await getDocs(documentsRef);
        setDocumentCount(querySnapshot.size);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching document count for knowledge:', error);
        }
        setDocumentCount(0);
      } finally {
        setLoadingDocumentCount(false);
      }
    };

    fetchDocumentCount();
  }, [knowledge.id]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else {
      navigate(`/knowledge/${knowledge.id}`);
    }
  };

  return (
    <div className={styles.knowledgeTile} onClick={handleClick}>
      {/* Knowledge icon */}
      <div className={styles.knowledgeIcon}>
        <img
          src={isDarkMode ? "/assets/icons/books.vertical.svg" : "/assets/icons/books.vertical.black.svg"}
          alt={t('knowledge_tile_alt')}
          width={24}
          height={24}
        />
      </div>

      {/* Knowledge information */}
      <div className={styles.knowledgeInfo}>
        <h3 className={styles.knowledgeTitle}>
          {knowledge.knowledgeName || t('knowledge_tile_unknown_name')}
        </h3>
        <p className={styles.knowledgeDescription}>
          {knowledge.knowledgeDescription || t('knowledge_tile_unknown_description')}
        </p>
      </div>

      {/* Document count badge */}
      <div className={styles.statusContainer}>
        <div className={`${styles.statusBadge} ${documentCount > 0 ? styles.statusDocuments : styles.statusEmpty}`}>
          {loadingDocumentCount ? t('project_tile_loading_documents') || '...' : `${documentCount} ${t('project_tile_documents_count') || 'documents'}`}
        </div>
      </div>
    </div>
  );
}

interface KnowledgeListProps {
  knowledge: Knowledge[];
  loading: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onAddKnowledge?: () => void;
}

export function KnowledgeList({
  knowledge,
  loading,
  searchValue,
  onSearchChange,
  onAddKnowledge
}: KnowledgeListProps) {
  const { t } = useLocale();

  const filteredKnowledge = knowledge.filter(item => {
    if (!searchValue) return true;
    const searchLower = searchValue.toLowerCase();
    return (
      item.knowledgeName?.toLowerCase().includes(searchLower) ||
      item.knowledgeDescription?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className={styles.knowledgeList}>
      <FilterBar
        filters={[]}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={t('knowledge_tile_search_placeholder')}
        showInviteButton={true}
        onAddUser={onAddKnowledge}
        addButtonText={t('knowledge_tile_add_knowledge')}
      />

      {loading ? (
        <div className={styles.loadingContainer}>
          <p>{t('knowledge_tile_loading')}</p>
        </div>
      ) : filteredKnowledge.length === 0 ? (
        <GlobalEmptyState />
      ) : (
        <div>
          {filteredKnowledge.map((item) => (
            <div key={item.id} className={styles.knowledgeItem}>
              <KnowledgeTile knowledge={item} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
